import { DestroyRef, Injectable, inject } from "@angular/core";
import { distinctUntilChanged, of, switchMap, tap } from "rxjs";
import { DateAdapter } from "@angular/material/core";
import { MenuOptionService } from "@app/core/services/menu-option.service";
import { AppInjector } from "@app/core/functions/app-injector";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ComponentOverridesService } from "@admin/application-settings/component-overrides/component-overrides.service";
import { ComponentOverridesLanguageSettings } from "@admin/application-settings/component-overrides/component-overrides.model";
import { FileDownload } from "@app/shared/models/file.model";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { EncodeDecodeService } from "@app/core/services/encode-decode.service";
import { TranslocoService, getBrowserCultureLang } from "@jsverse/transloco";

@Injectable({
  providedIn: "root"
})
export class I18nService {
  private _userSettingsService = inject(UserSettingsService);
  private _dateAdapter = inject<DateAdapter<any>>(DateAdapter<any>);
  private _systemSettingsService = inject(SystemSettingsService);
  private _defaultLanguage: string;
  private _supportedLanguages: string[];
  private _destroyRef = inject(DestroyRef);
  private _componentOverridesService: ComponentOverridesService = inject(ComponentOverridesService);
  private _fds: FileDownloadService = inject(FileDownloadService);
  private _ecd: EncodeDecodeService = inject(EncodeDecodeService);

  constructor(private _translateService: TranslocoService) {}

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */
  init(defaultLanguage: string, supportedLanguages: string[]) {
    this._defaultLanguage = defaultLanguage;
    this.translateService.setDefaultLang(this._defaultLanguage);
    this._supportedLanguages = supportedLanguages;
    this.language = "";

    this.translateService.langChanges$.pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged()).subscribe((lang: string) => {
      if (lang) {
        this._dateAdapter.setLocale(lang);
        this._userSettingsService.language = lang;
        this.menuOptionService.refreshMenus(); // fetch new menus based on language change
      }
    });
  }

  /**
   * Add json object to translation file.
   * @param language
   * @param jsonObject
   * @param merge
   */
  setTranslation(jsonObject: any) {
    if (!jsonObject) {
      return;
    }
    this.translateService.setTranslation(jsonObject, this.language, { merge: true });
  }

  /**
   * ## Translate a string resource
   *
   * To pass parameters to string resources use interpolateParams. The
   *  parameters should be a json data object.
   *
   * #### String resource example:
   *   - "LBL_DELETE_USER": "Are you sure user {{user}} should be deleted?"
   *
   * #### Data object example:
   *   - data = {user: "some user id"}
   *
   * #### Translate example:
   *   - const myString = this.translate("LBL_DELETE_USER", data);
   *
   * @param label
   * @param interpolateParams
   */
  translate(label: string, interpolateParams?: object) {
    if (!label) {
      return "";
    }

    return this.translateService.translate(label, interpolateParams);
  }

  /**
   * ## Translate a string resource
   *
   * Returns an observable that emits the translated string. This is useful for
   *  when you need to make sure the translation is downloaded and available
   *  before displaying the string.
   *
   * To pass parameters to string resources use interpolateParams. The
   *  parameters should be a json data object.
   *
   * #### String resource example:
   *   - "LBL_DELETE_USER": "Are you sure user {{user}} should be deleted?"
   *
   * #### Data object example:
   *   - data = {user: "some user id"}
   *
   * #### Translate example:
   *   - this.translateAsync("LBL_DELETE_USER", data).subscribe((result) => {
   *     const myString = result;
   *   });
   *
   * @param label
   * @param interpolateParams
   */
  translateAsync(label: string, interpolateParams?: object) {
    if (!label) {
      return of("");
    }

    return this.translateService.selectTranslate(label, interpolateParams);
  }

  /**
   * Reset language to fallback to user or system settings
   * @param language
   */
  resetLanguage(language: string = "") {
    this.language = language;
  }

  /**
   * Fetch file based on id and active language.
   */
  loadComponentText(componentId: string) {
    const parms = {
      componentId: componentId,
      language: {
        value: this.language
      },
      content: {
        value: ""
      }
    } as ComponentOverridesLanguageSettings;

    return this._componentOverridesService.fetchComponentLanguageById(parms).pipe(
      switchMap((result) => {
        if (result.content.value) {
          const payload: FileDownload = {
            file: result.content.value as string,
            type: FileTypes.TEXT
          };
          return this._fds.downloadFileAsString(payload, null, false);
        } else {
          return of("");
        }
      }),
      tap((result) => {
        if (result) {
          try {
            this.setTranslation(JSON.parse(this._ecd.decodeString(result)));
          } catch {}
        }
      })
    );
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {
    language =
      language ||
      this._userSettingsService.language ||
      this._systemSettingsService?.systemSettings?.localeSettings?.language ||
      getBrowserCultureLang();

    let isSupportedLanguage = this._supportedLanguages.includes(language);

    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split("-")[0];
      language = this._supportedLanguages.find((supportedLanguage) => supportedLanguage.startsWith(language)) || "";
      isSupportedLanguage = Boolean(language);
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this._defaultLanguage;
    }

    this.translateService.setActiveLang(language);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translateService.getActiveLang();
  }

  /**
   * @returns translation service
   */
  get translateService(): TranslocoService {
    return this._translateService;
  }

  get menuOptionService(): MenuOptionService {
    return AppInjector.get(MenuOptionService);
  }
}
